import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaTree, FaAward, FaUsers, FaRecycle, FaLaptop, FaCheck, FaRegCalendarAlt, FaArrowRight, FaBolt } from "react-icons/fa";
import { EKPED3_SCHOOL_ENABLED, SWACHHATA_SWM_REPORT_OPEN, EWASTE_DRIVE_OPEN, EWASTE_CARD_VISIBLE } from "../../config/campaigns";
import { isSc6Live, SC6_NAME, SC6_SCHOOL_PATH, SC6_TITLE, SC6_UNDER } from "../SpecialCampaign6/sc6Config";
import { isBricsLive, BRICS_CAMPAIGN, BRICS_DATES, BRICS_TITLE } from "./BricsCampaign/bricsConfig";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";

const openModal = (eventName) => () => window.dispatchEvent(new Event(eventName));

// One campaign card: accent bar, icon + status, headline metric (or a prompt),
// then the primary action full-width with secondary links beneath.
function ProgramCard({ tone, Icon, kicker, title, done, metric, unit, text, updatedAt, primary, secondary = [] }) {
  return (
    <div className={`sdh-program sdh-program--${tone}${done ? " is-done" : ""}`}>
      <Icon className="sdh-program__watermark" aria-hidden="true" />

      <div className="sdh-program__top">
        <span className="sdh-program__icon" aria-hidden="true"><Icon /></span>
        {done ? (
          <span className="sdh-pill sdh-pill--done"><FaCheck /> Submitted</span>
        ) : (
          <span className="sdh-pill sdh-pill--todo"><span className="sdh-pill__dot" /> Pending</span>
        )}
      </div>

      <div className="sdh-program__body">
        <span className="sdh-program__kicker">{kicker}</span>
        <h6 className="sdh-program__title">{title}</h6>
        {metric != null ? (
          <p className="sdh-program__metric">
            <strong>{metric.toLocaleString("en-IN")}</strong> <span>{unit}</span>
          </p>
        ) : (
          <p className="sdh-program__text">{text}</p>
        )}
        {updatedAt && (
          <p className="sdh-program__meta"><FaRegCalendarAlt /> Updated {fmtDate(updatedAt)}</p>
        )}
      </div>

      <div className="sdh-program__actions">
        {primary && (
          <button type="button" className="sdh-btn sdh-btn--primary" onClick={primary.onClick} title={primary.title}>
            {primary.label} {!done && <FaArrowRight className="sdh-btn__arrow" />}
          </button>
        )}
        {secondary.length > 0 && (
          <div className="sdh-program__links">
            {secondary.map(({ to, label, SIcon, title: linkTitle }) => (
              <Link key={to} to={to} className="sdh-btn sdh-btn--ghost" title={linkTitle}>
                <SIcon /> {label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function SchoolProgramCards({ plantation3Trees, swachhataSurvey, ewasteSummary, sc6Summary, bricsSummary }) {
  const navigate = useNavigate();
  const sc6Done = sc6Summary?.status === "submitted";
  const sc6Shown = isSc6Live() || sc6Done;
  // BRICS week: shown while the week is live, and afterwards to the schools
  // that took part so they keep their record.
  // "Done" now means the 7-question School Reporting form: that is what the
  // week is measured on. A school that only sent the two initiative forms is
  // still asked for it, so the card must not read as finished.
  const bricsDone = !!bricsSummary?.weekReport;
  const bricsAny = bricsSummary?.status === "submitted";
  const bricsShown = isBricsLive() || bricsAny;
  const treesDone = plantation3Trees > 0;
  const swachhataDone = swachhataSurvey?.status === "submitted";
  const ewasteDone = ewasteSummary?.status === "submitted";
  const ewasteItems = ewasteDone && ewasteSummary.collected === "yes" ? ewasteSummary.itemsTotal || 0 : null;

  return (
    <div className="sdh-programs">
      {/* National Event on 28 Oct 2026 under Special Campaign 6 — the form lives
          on its own dashboard page (full edit after login) */}
      {sc6Shown && (
      <ProgramCard
        tone="sc6"
        Icon={FaRecycle}
        kicker={SC6_UNDER}
        title={SC6_NAME}
        done={sc6Done}
        metric={sc6Done ? sc6Summary.items : null}
        unit={sc6Summary?.items === 1 ? "E-Waste item collected" : "E-Waste items collected"}
        text={sc6Done ? `Participation details uploaded (Ack. No. ${sc6Summary.ackId}). You can edit them here.` : `${SC6_TITLE} — upload your school’s participation details.`}
        updatedAt={sc6Done ? sc6Summary.submittedAt : null}
        primary={{ label: sc6Done ? "Edit Details" : "Fill Form", onClick: () => navigate(SC6_SCHOOL_PATH), title: SC6_NAME }}
      />
      )}

      {bricsShown && (
      <ProgramCard
        tone="sc6"
        Icon={FaBolt}
        kicker={BRICS_DATES}
        title={BRICS_CAMPAIGN}
        done={bricsDone}
        text={
          bricsDone
            ? "School Reporting form submitted. You can edit your answers here."
            : bricsAny
            ? "Initiative details saved — the 7-question School Reporting form is still to be filled."
            : `${BRICS_TITLE} — answer the 7 School Reporting questions.`
        }
        updatedAt={bricsAny ? bricsSummary.submittedAt : null}
        primary={{ label: bricsDone ? "Edit Details" : "Fill Form", onClick: openModal("openBricsModal"), title: BRICS_CAMPAIGN }}
      />
      )}

      {/* Ek Ped Maa Ke Naam 3.0 — switched off for schools (config/campaigns.js) */}
      {EKPED3_SCHOOL_ENABLED && (
      <ProgramCard
        tone="tree"
        Icon={FaTree}
        kicker="Plantation drive"
        title="Ek Ped Maa Ke Naam 3.0"
        done={treesDone}
        metric={treesDone ? plantation3Trees : null}
        unit={plantation3Trees === 1 ? "sapling planted" : "saplings planted"}
        text="Record the saplings your school planted and get your certificate."
        primary={{ label: treesDone ? "Update Details" : "Add Details", onClick: openModal("openEkped3Modal") }}
        secondary={treesDone ? [{ to: "/school/certificate-3", label: "Certificate", SIcon: FaAward }] : []}
      />
      )}

      {/* SWM report closed (config/campaigns.js): only schools that submitted
          still see the card, for their certificate — no form button. */}
      {SWACHHATA_SWM_REPORT_OPEN ? (
      <ProgramCard
        tone="shs"
        Icon={FaRecycle}
        kicker="Cleanliness campaign"
        title="Swachhata Hi Seva 2026"
        done={swachhataDone}
        text={
          swachhataDone
            ? "Your Solid Waste Management report is submitted. Invite students to take the quiz."
            : "Fill in your school's Solid Waste Management report."
        }
        updatedAt={swachhataDone ? swachhataSurvey.submittedAt : null}
        primary={{
          label: swachhataDone ? "Update Details" : "Fill Form",
          onClick: openModal("openSwachhataModal"),
          title: "Swachhata Hi Seva 2026: Solid Waste Management report",
        }}
        secondary={[
          ...(swachhataDone
            ? [{ to: "/school/swachhata-certificate", label: "Certificate", SIcon: FaAward, title: "Download your Swachhata Hi Seva participation certificate" }]
            : []),
          { to: "/school/swachhata-students", label: "Students", SIcon: FaUsers },
        ]}
      />
      ) : swachhataDone && (
      <ProgramCard
        tone="shs"
        Icon={FaRecycle}
        kicker="Cleanliness campaign"
        title="Swachhata Hi Seva 2026"
        done
        text="Swachhata Hi Seva 2026 is closed. Your Solid Waste Management report was submitted."
        updatedAt={swachhataSurvey.submittedAt}
        secondary={[
          { to: "/school/swachhata-certificate", label: "Certificate", SIcon: FaAward, title: "Download your Swachhata Hi Seva participation certificate" },
          { to: "/school/swachhata-students", label: "Students", SIcon: FaUsers },
        ]}
      />
      )}

      {/* Off the signed-in dashboard since 3 Nov (EWASTE_CARD_VISIBLE): the
          drive closed on 31 Oct, the admin reports still hold every figure and
          nothing is deleted. Flip the flag to bring the card back. */}
      {EWASTE_CARD_VISIBLE && (EWASTE_DRIVE_OPEN || ewasteDone) && (
      <ProgramCard
        tone="ewaste"
        Icon={FaLaptop}
        kicker="Collection drive"
        title="E-Waste Collection Drive"
        done={ewasteDone}
        metric={ewasteItems}
        unit={ewasteItems === 1 ? "E-Waste item collected" : "E-Waste items collected"}
        text={
          ewasteDone
            ? EWASTE_DRIVE_OPEN
              ? "Your E-Waste activity is reported."
              : "Your E-Waste activity is reported. The drive is now closed."
            : "Report the E-Waste your school collected, item-wise."
        }
        updatedAt={ewasteDone ? ewasteSummary.submittedAt : null}
        primary={
          EWASTE_DRIVE_OPEN
            ? {
                label: ewasteDone ? "Update Details" : "Fill Form",
                onClick: openModal("openEWasteModal"),
                title: "E-Waste Collection Drive report",
              }
            : null
        }
      />
      )}
    </div>
  );
}
